import React from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

export type ScheduleDelayOptions = {
  schedule_delay_enabled?: string;
  schedule_delay_amount?: string;
  schedule_delay_unit?: string;
  schedule_delay_reason?: string;
};

interface ScheduleDelaySectionProps {
  enabled: boolean;
  options: ScheduleDelayOptions;
  onChange: (key: keyof ScheduleDelayOptions, value: string) => void;
}

const checkboxSx = {
  "&.Mui-checked": { color: "#705D4A" },
  "& .MuiSvgIcon-root": { fontSize: 20 },
};

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    fontFamily: "var(--theo-font-sans)",
    "&.Mui-focused fieldset": { borderColor: "#705D4A", borderWidth: 1.5 },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#9a8a7a" },
    "& .MuiInputBase-input": { color: "#333", cursor: "text" },
  },
};

const ScheduleDelaySection: React.FC<ScheduleDelaySectionProps> = ({
  enabled,
  options,
  onChange,
}) => {
  if (!enabled) return null;

  const delayEnabled = options.schedule_delay_enabled === "true";

  const handleToggle = (checked: boolean) => {
    if (!checked) {
      onChange("schedule_delay_enabled", "false");
      return;
    }
    onChange("schedule_delay_enabled", "true");
    if (!options.schedule_delay_amount) onChange("schedule_delay_amount", "1");
    if (!options.schedule_delay_unit) onChange("schedule_delay_unit", "hours");
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: "#FAFAF9",
        border: "1px solid #F0EDE9",
      }}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={delayEnabled}
            onChange={(event) => handleToggle(event.target.checked)}
            sx={checkboxSx}
          />
        }
        label={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccessTimeIcon sx={{ fontSize: 18, color: "#888" }} />
            <span>Run this step after a delay</span>
          </Box>
        }
        sx={{ m: 0, "& .MuiFormControlLabel-label": { fontFamily: "var(--theo-font-sans)", fontSize: "0.9375rem" } }}
      />
      {delayEnabled && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1.5 }}>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <TextField
              size="small"
              type="number"
              label="Delay"
              value={options.schedule_delay_amount ?? ""}
              onChange={(event) => onChange("schedule_delay_amount", event.target.value)}
              sx={{ ...textFieldSx, flex: "1 1 140px" }}
              inputProps={{ min: 1, step: 1, style: { fontFamily: "var(--theo-font-sans)" } }}
              InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
            />
            <TextField
              select
              size="small"
              label="Unit"
              value={options.schedule_delay_unit || "hours"}
              onChange={(event) => onChange("schedule_delay_unit", event.target.value)}
              sx={{ ...textFieldSx, flex: "1 1 160px" }}
              InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
            >
              <MenuItem value="seconds">Seconds</MenuItem>
              <MenuItem value="minutes">Minutes</MenuItem>
              <MenuItem value="hours">Hours</MenuItem>
              <MenuItem value="days">Days</MenuItem>
            </TextField>
          </Box>
          <TextField
            size="small"
            fullWidth
            label="Reason (optional)"
            placeholder="e.g. wait for mailbox provisioning"
            value={options.schedule_delay_reason ?? ""}
            onChange={(event) => onChange("schedule_delay_reason", event.target.value)}
            sx={textFieldSx}
            inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
            InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ScheduleDelaySection;
